import { apiRequest } from "@/lib/queryClient";
import {
  getFeatureMetadata,
  getFeatureUsageForPath,
  type FeatureKey,
} from "@scroll/shared-utils";

interface AnalyticsEvent {
  eventName: string;
  eventData?: Record<string, unknown>;
  sessionId?: string;
  platform?: string;
}

const eventBuffer: AnalyticsEvent[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;
const SESSION_ID = Math.random().toString(36).substring(2);
const MAX_BATCH_SIZE = 50;

function scheduleFlush() {
  if (flushTimer) return;
  flushTimer = setTimeout(flush, 10_000);
}

async function flush() {
  flushTimer = null;
  if (eventBuffer.length === 0) return;
  const events = eventBuffer.splice(0, MAX_BATCH_SIZE);
  try {
    await apiRequest("POST", "/api/analytics/events", { events });
  } catch {
    // Silently fail - analytics should never break the app
  }
}

function flushWithBeacon() {
  if (typeof navigator === "undefined" || typeof navigator.sendBeacon !== "function") {
    void flush();
    return;
  }

  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  if (eventBuffer.length === 0) return;

  const events = eventBuffer.splice(0, MAX_BATCH_SIZE);
  const blob = new Blob([JSON.stringify({ events })], { type: "application/json" });
  const queued = navigator.sendBeacon("/api/analytics/events", blob);
  if (!queued) eventBuffer.unshift(...events);
}

export function flushAnalytics() {
  return flush();
}

export function track(eventName: string, eventData?: Record<string, unknown>) {
  eventBuffer.push({
    eventName,
    eventData,
    sessionId: SESSION_ID,
    platform: "web",
  });

  if (eventBuffer.length >= MAX_BATCH_SIZE) {
    flush();
  } else {
    scheduleFlush();
  }
}

export function trackFeatureUsage(
  feature: FeatureKey | string,
  action: string,
  eventData?: Record<string, unknown>,
) {
  const metadata = getFeatureMetadata(feature);
  track("feature_used", {
    ...eventData,
    ...metadata,
    action,
  });
}

export function trackScreenView(path: string, eventData?: Record<string, unknown>) {
  const usage = getFeatureUsageForPath(path);
  const data = {
    ...eventData,
    ...usage,
    source: "screen_view",
  };

  track("screen_viewed", data);
  trackFeatureUsage(usage.feature, "view", data);
}

// Flush on page unload
if (typeof window !== "undefined") {
  window.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") flushWithBeacon();
  });
  window.addEventListener("pagehide", flushWithBeacon);
}
